import { uploadFileToAzureBlob } from "@services/azureDataApi";
import { SasTokenPrefix, SasTokenPrivilege } from "@services/user/types";
import { getSasToken } from "@services/userApi";
import { useMutation } from "@tanstack/react-query";
import { runTransaction } from "@telemetry/runTransaction";

interface UploadParams {
  files: File[];
  startIdx: number;
}

export const useUploadOtherLicenseDocuments = ({
  handleUploadSuccess,
  handleUploadError,
  telemetryScreen = "PortOperatorLicenseRequest",
}: {
  handleUploadSuccess?: (data: { documentUrls: string[]; startIdx: number; files: File[] }) => void;
  handleUploadError?: (
    error: Error,
    meta: { startIdx: number; count: number; fileNames: string[] },
  ) => void;
  telemetryScreen?: string;
}) => {
  const mutation = useMutation({
    mutationFn: ({ files, startIdx }: UploadParams) =>
      runTransaction(
        {
          module: "licensing",
          screen: telemetryScreen,
          action: "upload_license_document",
          entityType: "license_document",
        },
        async (transactionId) => {
          const documentUrls: string[] = [];

          for (const file of files) {
            const { blobUrl, sasToken } = await getSasToken(
              {
                prefix: SasTokenPrefix.LICENSE_SERVICE,
                contentType: file.type || null,
                privilege: SasTokenPrivilege.WRITE,
                blobUrl: null,
              },
              ...(transactionId ? [transactionId] : []),
            );

            const result = await uploadFileToAzureBlob({
              blobUrl,
              sasToken,
              file,
              certificateType: "OTHERS",
            });

            result.forEach((r) => documentUrls.push(r.documentUrl));
          }

          return { documentUrls, startIdx, files };
        },
      ),
    onSuccess: (data) => handleUploadSuccess?.(data),
    onError: (error: Error, variables: UploadParams) => {
      handleUploadError?.(error, {
        startIdx: variables.startIdx,
        count: variables.files.length,
        fileNames: variables.files.map((f) => f.name),
      });
    },
  });

  return { ...mutation, isLoading: mutation.isPending };
};
